import { RiMailSendLine } from "@remixicon/react";
import { Button } from "@screener/uikit/components/button";
import { IconTile } from "@screener/uikit/components/icon-tile";
import { useCallback, useEffect, useRef, useState } from "react";
import { type ResendConfirmationResult, resendConfirmation } from "@/lib/auth-api";
import { AuthHeading } from "./auth-layout";

/** Состояние повтора письма: пароль для него не нужен и не хранится. */
export type ResendState = "idle" | "pending" | ResendConfirmationResult;

/**
 * Текст под кнопкой повтора. Ответ `202` общий при любом исходе (stage9-email-
 * confirmation, Р9), поэтому не утверждает, что письмо ушло. `switch` с
 * `satisfies never`: новый исход не уйдёт к человеку без сообщения.
 */
export function describeResend(state: ResendState): string | null {
  switch (state) {
    case "idle":
    case "pending":
      return null;
    case "accepted":
      return "Если почта ждёт подтверждения, мы отправили новое письмо.";
    case "rate-limited":
      return "Слишком много попыток. Подождите и попробуйте снова.";
    case "unavailable":
      return "Не удалось отправить письмо, попробуйте позже.";
    default:
      return state satisfies never;
  }
}

/**
 * Повтор письма: уходит только логин (пароль для повтора не нужен и не
 * хранится, spec `email-confirmation`). Отправитель — параметр.
 */
export function submitResend(
  login: string,
  send: (login: string) => Promise<ResendConfirmationResult> = resendConfirmation,
): Promise<ResendConfirmationResult> {
  return send(login);
}

interface ConfirmEmailViewProps {
  /** Адрес для показа: введённый в форме или маска из ответа демона. */
  email: string;
  /** Демон завёл запись, но письмо не ушло. */
  mailUnavailable: boolean;
  resend: ResendState;
  onResend: () => void;
  onBack: () => void;
}

/**
 * Экран «Подтвердите почту» (общий для регистрации и входа). Он сменяет форму,
 * и фокус форма уносит с собой, поэтому при появлении фокус переходит на
 * заголовок: скринридер читает новый экран с начала, клавиатура не теряется.
 */
export function ConfirmEmailView({
  email,
  mailUnavailable,
  resend,
  onResend,
  onBack,
}: ConfirmEmailViewProps) {
  const resendMessage = describeResend(resend);
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);
  return (
    <div data-slot="confirm-email" className="flex flex-col items-center gap-5 text-center">
      <IconTile>
        <RiMailSendLine className="size-[18px]" />
      </IconTile>
      <AuthHeading title="Подтвердите почту" headingRef={headingRef}>
        <p className="text-[13px] leading-normal text-muted-foreground">
          {mailUnavailable
            ? `Письмо на адрес ${email} не удалось отправить`
            : `Письмо отправлено на адрес ${email}`}
        </p>
      </AuthHeading>
      <Button
        type="button"
        size="lg"
        variant="outline"
        className="w-full"
        disabled={resend === "pending"}
        onClick={onResend}
      >
        Отправить письмо ещё раз
      </Button>
      {/* Регион в DOM всегда: скринридер объявляет только изменение текста в уже существующем. */}
      <p role="status" className="text-xs leading-[1.4] text-muted-foreground empty:hidden">
        {resendMessage}
      </p>
      <Button type="button" variant="link" size="sm" onClick={onBack}>
        Вернуться ко входу
      </Button>
    </div>
  );
}

interface ConfirmEmailScreenProps {
  /** Логин, по которому просится повтор; пароль не передаётся и не хранится. */
  login: string;
  email: string;
  mailUnavailable?: boolean;
  onBack: () => void;
  /** Отправитель повтора — параметр, чтобы проверять без сети. */
  resendRequest?: (login: string) => Promise<ResendConfirmationResult>;
}

/**
 * Экран «Подтвердите почту» с повтором письма по логину. Один на регистрацию
 * и вход (stage9-email-confirmation, Р8–Р9).
 */
export function ConfirmEmailScreen({
  login,
  email,
  mailUnavailable = false,
  onBack,
  resendRequest = resendConfirmation,
}: ConfirmEmailScreenProps) {
  const [resend, setResend] = useState<ResendState>("idle");
  const [unavailableNote, setUnavailableNote] = useState(mailUnavailable);

  const handleResend = useCallback(() => {
    if (resend === "pending") {
      return;
    }
    setResend("pending");
    submitResend(login, resendRequest).then((result) => {
      setResend(result);
      // Прежнее «письмо не ушло» относилось к первой отправке; после повтора
      // о нём говорит общий ответ.
      setUnavailableNote(false);
    });
  }, [login, resend, resendRequest]);

  return (
    <ConfirmEmailView
      email={email}
      mailUnavailable={unavailableNote}
      resend={resend}
      onResend={handleResend}
      onBack={onBack}
    />
  );
}
